import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  normalizeWorkloadGpuUtilization,
  NormalizedWorkloadGpuUtilizationSummary,
} from '@cast/split-me/features/cluster/cost-report';
import { useWorkloadGpuUtilizationSummaryQuery } from '@cast/split-me/hooks';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isRangeSupported } from '../../_utils/isRangeSupported';

type EmptyGpuUtilizationDataParams = {
  clusterId: string;
  workloadName: string | undefined;
  workloadType: string | undefined;
  namespace: string | undefined;
};

const emptyGpuUtilizationData = ({
  clusterId,
  workloadName,
  workloadType,
  namespace,
}: EmptyGpuUtilizationDataParams): NormalizedWorkloadGpuUtilizationSummary => ({
  clusterId,
  workloadName: workloadName || '',
  workloadType: workloadType || '',
  namespace: namespace || '',
  avgGpuUtilization: 0,
  avgGpuMemoryUtilization: 0,
  totalGpuMemoryBytes: 0,
  totalUsedGpuMemoryBytes: 0,
  totalWastedGpuMemoryBytes: 0,
  totalUptimeMinutes: 0,
  totalIdleMinutes: 0,
  totalCostImpact: undefined,
  intervals: [],
});

export const useInventoryWorkloadGpuData = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { from, to } = useInventoryWorkloadsTimeRangeSelection();

  const fromParam = from ? dayjs.utc(from) : dayjs.utc(0);
  const toParam = to ? dayjs.utc(to) : dayjs.utc(0);
  const hasRange = !!from && !!to;
  const rangeSupported = hasRange && isRangeSupported(from, to);

  const {
    data: rawData,
    isLoading,
    error,
    refetch,
  } = useWorkloadGpuUtilizationSummaryQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace || '',
    workloadName: workload?.name || '',
    workloadType: workload?.kind || '',
    startTime: fromParam.toISOString(),
    endTime: toParam.toISOString(),
    useListingPrices: false,
    enabled:
      rangeSupported &&
      !!workload?.namespace &&
      !!workload?.kind &&
      !!workload?.name,
  });

  const data = useMemo(() => {
    if (!rawData) {
      return emptyGpuUtilizationData({
        clusterId: cluster.id,
        workloadName: workload?.name,
        workloadType: workload?.kind,
        namespace: workload?.namespace,
      });
    }

    return normalizeWorkloadGpuUtilization(rawData);
  }, [
    rawData,
    cluster.id,
    workload?.name,
    workload?.kind,
    workload?.namespace,
  ]);

  return {
    data,
    isLoading,
    error,
    refetch,
    isRangeSupported: rangeSupported,
  };
};
